import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { retrieveToken } from "../../../utils/storage";
import { endpoint } from "../../../utils/constants/endpoint";

export const adminUsersApi = createApi({
  reducerPath: "adminUsers",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
  }),
  tagTypes: ["users", "superUsers", "cf", "cfAll"],
  endpoints: (builder) => ({
    getUsers: builder.query({
      query: ({
        page: { startRow, endRow },
        value = "",
        accessToken = "",
        filter = {
          studioId: "",
          userCompanyId: "",
          adminId: "",
          service: "",
          employment: "",
          withDocuments: "",
        },
        extraFilters = {},
        orderDirection = "desc",
        valueToOrderBy = "lastDocUploadedAt",
      }) => ({
        url: `admin/users/list?query=${value.toLowerCase()}&studioId=${filter.studioId
          }&userCompanyId=${filter.userCompanyId}&adminId=${filter.adminId
          }&serviceId=${filter.service}&employmentStatusId=${filter.employment
          }&withDocuments=${filter.withDocuments}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken || retrieveToken()}`,
        },
        body: {
          startRow,
          endRow,
          sort: [
            {
              colId: valueToOrderBy,
              sortDirection: orderDirection,
              sortIndex: 0,
            },
          ],
          filter: {
            ...extraFilters,
          },
        },
      }),
      providesTags: ["users"],
    }),
    getUserById: builder.query({
      query: (id) => ({
        url: "admin/users/" + id,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["users", "superUsers"],
    }),
    getUserIdDocuments: builder.query({
      query: ({ page: { startRow, endRow }, userId }) => ({
        url: `admin/documents/list`,
        method: "POST",
        params: { userId },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: { startRow, endRow },
      }),
    }),
    getUserIdFiscalCodeDoc: builder.query({
      query: ({ id, cf }) => ({
        url: `/admin/users/${id}/documents?fiscalCode=${cf}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
    }),
    getUserAllCF: builder.query({
      query: (id) => ({
        url: "/admin/users/" + id + "/allcf",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["cf", "cfAll"]
    }),
    getAllChildrens: builder.query({
      query: (userId) => ({
        url: `/admin/users/${userId}/details/children`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["cf", "cfAll"]
    }),
    getAllFamilies: builder.query({
      query: (userId) => ({
        url: `/admin/users/${userId}/details/familymembers`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["cf", "cfAll"]
    }),
    createUser: builder.mutation({
      query: (userData) => ({
        url: "/admin/users/create",
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: userData,
      }),
      invalidatesTags: ["users", "superUsers"],
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: endpoint.ADMIN_USERS.ADMIN_USERS + "/" + id,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: id,
      }),
      invalidatesTags: ["users", "superUsers"],
    }),
    editUser: builder.mutation({
      query: (body) => ({
        url: endpoint.ADMIN_USERS.ADMIN_USERS + "/" + body.id,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["users", "superUsers", "cf", "cfAll"],
    }),
    associateUser: builder.mutation({
      query: (userId) => ({
        url: `/admin/users/${userId}/confirmoperatorassociation`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: userId,
      }),
      invalidatesTags: ["users"],
    }),
    reactiveEmail: builder.mutation({
      query: ({ idUser }) => ({
        url: `/admin/users/${idUser}/regresend`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: idUser,
      }),
      invalidatesTags: ["users"],
    }),
    getDetailsFamily: builder.query({
      query: (userId) => ({
        url: `/admin/users/${userId}/details/familymembers`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["user"],
    }),
    getDetailsChildren: builder.query({
      query: (userId) => ({
        url: `/admin/users/${userId}/details/children`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
      }),
      providesTags: ["user"],
    }),
    doDeleteFamily: builder.mutation({
      query: (body) => ({
        url: `/admin/users/${body.userId}/details/familymembers/${body.id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["user"],
    }),
    doDeleteChildren: builder.mutation({
      query: (body) => ({
        url: `/admin/users/${body.userId}/details/children/${body.id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["user"],
    }),

    doCreateFamily: builder.mutation({
      query: (body) => ({
        url: `/admin/users/${body.userId}/details/familymembers/add`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["user"],
    }),

    doCreateChildren: builder.mutation({
      query: (body) => ({
        url: `/admin/users/${body.userId}/details/children/add`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["user"],
    }),

    doEditFamily: builder.mutation({
      query: (body) => ({
        url: `/admin/users/${body.userId}/details/familymembers/${body.Id}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["user"],
    }),

    doEditChildren: builder.mutation({
      query: (body) => ({
        url: `/admin/users/${body.userId}/details/children/${body.Id}`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${retrieveToken()}`,
        },
        body: body,
      }),
      invalidatesTags: ["user"],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetUserIdDocumentsQuery,
  useGetUserIdFiscalCodeDocQuery,
  useGetAllChildrensQuery,
  useGetAllFamiliesQuery,
  useGetUserAllCFQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useEditUserMutation,
  useAssociateUserMutation,
  useReactiveEmailMutation,
  useGetDetailsChildrenQuery,
  useGetDetailsFamilyQuery,
  useDoDeleteChildrenMutation,
  useDoDeleteFamilyMutation,
  useDoCreateFamilyMutation,
  useDoCreateChildrenMutation,
  useDoEditChildrenMutation,
  useDoEditFamilyMutation,
} = adminUsersApi;
